import type {
  WorkspaceIntroCardProps,
  WorkspaceIntroSection,
  WorkspaceIntroStep,
} from '../../../shared/ui/common/WorkspaceIntroCard';
import type {
  WorkspaceSpotlightTourDetail,
  WorkspaceSpotlightTourStep,
} from '../../../shared/ui/common/WorkspaceSpotlightTour';

interface MappingWorkspaceGuideContentArgs {
  selectedProfileName: string;
  scopeCount: number;
  totalMappings: number;
  mappedMappings: number;
  requiredOpen: number;
  workspaceReady: boolean;
}

interface MappingWorkspaceGuideRefs {
  mobileMenuButton: WorkspaceSpotlightTourStep['targetRef'];
  navigation: WorkspaceSpotlightTourStep['targetRef'];
  selectedObjectTypeNav: WorkspaceSpotlightTourStep['targetRef'];
  generalNav: WorkspaceSpotlightTourStep['targetRef'];
  processConditionsNav: WorkspaceSpotlightTourStep['targetRef'];
  projectParametersNav: WorkspaceSpotlightTourStep['targetRef'];
  statusBuilderNav: WorkspaceSpotlightTourStep['targetRef'];
  statusCategoryNav: WorkspaceSpotlightTourStep['targetRef'];
  mappingCanvas: WorkspaceSpotlightTourStep['targetRef'];
  tableControls: WorkspaceSpotlightTourStep['targetRef'];
  pcfColumn: WorkspaceSpotlightTourStep['targetRef'];
  revitColumn: WorkspaceSpotlightTourStep['targetRef'];
  typeColumn: WorkspaceSpotlightTourStep['targetRef'];
  formulaColumn: WorkspaceSpotlightTourStep['targetRef'];
  rowActions: WorkspaceSpotlightTourStep['targetRef'];
  generalSection: WorkspaceSpotlightTourStep['targetRef'];
  processConditions: WorkspaceSpotlightTourStep['targetRef'];
  projectParameters: WorkspaceSpotlightTourStep['targetRef'];
  advancedSection: WorkspaceSpotlightTourStep['targetRef'];
  statusBuilderSection: WorkspaceSpotlightTourStep['targetRef'];
  statusCategorySection: WorkspaceSpotlightTourStep['targetRef'];
  footerActions: WorkspaceSpotlightTourStep['targetRef'];
}

interface MappingWorkspaceTourContentArgs {
  refs: MappingWorkspaceGuideRefs;
  isMobile: boolean;
  objectTypeTargetName?: string;
  /** When set, adds navigator + canvas spotlight steps for the first status category row. */
  firstStatusCategoryId?: string | null;
}

/** Tour steps that highlight the mapping navigator; the tree rail should stay expanded for readable targets. */
export const MAPPING_WORKSPACE_TOUR_TREE_FOCUS_STEP_IDS: readonly string[] = [
  'navigation',
  'navigator-general',
  'navigator-process-conditions',
  'navigator-project-parameters',
  'navigator-object-types',
  'navigator-status-builder',
  'navigator-status-category',
] as const;

export const isMappingTourTreeFocusStep = (stepId: string | undefined): boolean =>
  Boolean(stepId && (MAPPING_WORKSPACE_TOUR_TREE_FOCUS_STEP_IDS as readonly string[]).includes(stepId));

const section = (title: string, bullets: string[]): WorkspaceIntroSection => ({
  title,
  bullets,
});

const step = (label: string, goal: string, checks?: string[]): WorkspaceIntroStep => ({
  label,
  goal,
  checks,
});

const detail = (
  label: string,
  bullets: string[],
  tone: WorkspaceSpotlightTourDetail['tone'] = 'default'
): WorkspaceSpotlightTourDetail => ({
  label,
  bullets,
  tone,
});

const TOUR_PHASES = {
  navigator: 'Navigator',
  canvas: 'Canvas',
  sections: 'Sections',
  handoff: 'Handoff',
} as const;

const TOUR_RELATED_LABELS = {
  scope: 'Scope',
} as const;

export const buildMappingWorkspaceGuideContent = ({
  selectedProfileName,
  scopeCount,
  totalMappings,
  mappedMappings,
  requiredOpen,
  workspaceReady,
}: MappingWorkspaceGuideContentArgs): WorkspaceIntroCardProps => ({
  eyebrow: 'Mapping workspace guide',
  title: "Define the profile's PCF mappings",
  summary:
    'Map each PCF attribute to its source: Revit, BOM, a direct value, or a formula for the active profile.',
  hint:
    'Confirm the active profile and mapping scope, then review the current values before changing a mapping.',
  status: workspaceReady
    ? 'Required mapping keys are covered'
    : `${requiredOpen} required mapping key${requiredOpen === 1 ? '' : 's'} still open`,
  actionLabel: 'Start tour',
  chips: [
    `${scopeCount} owning scopes`,
    `${mappedMappings}/${totalMappings} mappings resolved`,
    workspaceReady ? 'Required keys covered' : `${requiredOpen} required keys open`,
    selectedProfileName ? `Active profile: ${selectedProfileName}` : 'Active profile context',
  ],
  sections: [
    section('Use Mapping for', [
      'Assigning each PCF attribute to the appropriate scope and value source.',
      'Reviewing values provided by the profile before creating an object-specific mapping override.',
      'Defining how each mapping row contributes to PCF output before starting repeated model cleanup.',
    ]),
    section('Do not use Mapping for', [
      'Correcting the same missing or incorrect Revit value across multiple families.',
      'Repeated Revit data cleanup once the source-to-PCF mapping is already defined. Use Quick Fill instead.',
    ]),
    section('Check before editing', [
      'Confirm the active profile and the relevant mapping scope: object type, General, Process conditions, Project properties, Advanced settings, or Project phase statuses.',
      'Review the PCF target, required status, source type, and current value together before changing a mapping.',
      'Keep scope and status-category settings separate from editable descriptions and notes.',
      'Identify whether the current value comes from Revit, BOM, a direct attribute, or a formula before replacing it.',
    ]),
    section('Ready to continue', [
      'Leave Mapping when each PCF target has a clearly defined source and mapping.',
      'Continue in Quick Fill for repeated Revit data corrections, then run Validation to confirm export readiness.',
    ]),
  ],
  steps: [
    step('1. Confirm the profile', 'Identify the profile that provides the default mapping values for this workspace.', [
      'The intended profile is active.',
      'You know which values come from the profile and which were changed locally.',
    ]),
    step('2. Select the mapping scope', 'Choose the relevant scope: object type, General, Process conditions, Project properties, Advanced settings, or Project phase statuses.', [
      'The selected scope is appropriate for both the source value and its PCF output.',
    ]),
    step('3. Inspect the current mapping', 'Review the PCF target and current value source before deciding whether an override is needed.', [
      'The current value source is understood.',
      'The change belongs in Mapping rather than in Revit data or Quick Fill.',
    ]),
    step('4. Define the mapping', 'Set the PCF target and choose its source: Revit, BOM, a direct value, or a formula.', [
      'Every required PCF attribute has a defined source.',
      'Each override has a clear effect on the PCF output.',
    ]),
    step('5. Continue to the next step', 'Use Apply mapping and stay to keep reviewing, or Save mapping and close when the mapping is complete and you are ready to continue.', [
      'Repeated Revit data corrections are handled in Quick Fill.',
      'Validation can evaluate the finalized mapping configuration.',
    ]),
  ],
});

export const buildMappingWorkspaceTourSteps = ({
  refs,
  isMobile,
  objectTypeTargetName,
  firstStatusCategoryId,
}: MappingWorkspaceTourContentArgs): WorkspaceSpotlightTourStep[] => {
  const objectTypeNode = objectTypeTargetName
    ? {
        type: 'objectType' as const,
        name: objectTypeTargetName,
      }
    : undefined;
  const selectedScopeRelatedTarget = {
    label: isMobile ? 'Mobile scope menu' : 'Owning object type in the navigator',
    visualLabel: isMobile ? 'Scope menu' : objectTypeTargetName || 'Owning object type',
    targetRef: isMobile ? refs.mobileMenuButton : refs.selectedObjectTypeNav,
    fallbackRect: isMobile
      ? {
          top: 168,
          left: 16,
          width: 44,
          height: 44,
        }
      : {
          top: 230,
          left: 28,
          width: 252,
          height: 48,
        },
  };
  const generalRelatedTarget = {
    label: isMobile ? 'Mobile scope menu' : 'General scope in the navigator',
    visualLabel: isMobile ? 'Scope menu' : 'General',
    targetRef: isMobile ? refs.mobileMenuButton : refs.generalNav,
  };
  const processConditionsRelatedTarget = {
    label: isMobile ? 'Mobile scope menu' : 'Process conditions in the navigator',
    visualLabel: isMobile ? 'Scope menu' : 'Process conditions',
    targetRef: isMobile ? refs.mobileMenuButton : refs.processConditionsNav,
  };
  const projectPropertiesRelatedTarget = {
    label: isMobile ? 'Mobile scope menu' : 'Project properties in the navigator',
    visualLabel: isMobile ? 'Scope menu' : 'Project properties',
    targetRef: isMobile ? refs.mobileMenuButton : refs.projectParametersNav,
  };
  const advancedRelatedTarget = {
    label: isMobile ? 'Mobile scope menu' : 'Advanced settings in the navigator',
    visualLabel: isMobile ? 'Scope menu' : 'Advanced settings',
    targetRef: isMobile ? refs.mobileMenuButton : refs.navigation,
  };
  const statusBuilderRelatedTarget = {
    label: isMobile ? 'Mobile scope menu' : 'Project phase statuses in the navigator',
    visualLabel: isMobile ? 'Scope menu' : 'Project phase statuses',
    targetRef: isMobile ? refs.mobileMenuButton : refs.statusBuilderNav,
  };
  const statusCategoryRelatedTarget = {
    label: isMobile ? 'Mobile scope menu' : 'Status category in the navigator',
    visualLabel: isMobile ? 'Scope menu' : 'Status category',
    targetRef: isMobile ? refs.mobileMenuButton : refs.statusCategoryNav,
  };

  const mobileMenuStep: WorkspaceSpotlightTourStep = {
    id: 'mobile-mapping-menu',
    phaseLabel: TOUR_PHASES.navigator,
    title: 'Open the scope navigator',
    focusLabel: 'Mobile scope navigator button',
    intro: 'On a small screen, the menu opens the scope tree so you can select the owner before editing a mapping.',
    details: [
      detail('How to use it', [
        'Open the menu to choose an object type, General, Process conditions, Project properties, or Advanced settings.',
        'Close the menu after selecting the owner so the mapping canvas remains visible.',
      ]),
    ],
    nextAction: "Select the owning scope, close the menu, and review that scope's mappings.",
    targetRef: refs.mobileMenuButton,
    fallbackRect: {
      top: 168,
      left: 16,
      width: 44,
      height: 44,
    },
  };

  const navigationStep: WorkspaceSpotlightTourStep = {
    id: 'navigation',
    phaseLabel: TOUR_PHASES.navigator,
    title: 'Choose the correct mapping scope',
    focusLabel: 'Mapping scope navigator',
    intro: 'The navigator separates object-specific mappings from shared, process, project, and status settings.',
    details: [
      detail('Choose the scope', [
        'Select the scope that provides the value or controls how it is used in the PCF output.',
        'Keep object, process, project, and status settings in their corresponding sections.',
      ]),
      detail('Keep mappings in the correct scope', [
        'Do not place project or process metadata in an object mapping when dedicated scopes are available for those values.',
      ], 'caution'),
    ],
    nextAction: 'Open the relevant scope, then review only the mappings whose source and PCF output belong there.',
    targetRef: refs.navigation,
    fallbackRect: {
      top: 176,
      left: 28,
      width: 270,
      height: 520,
    },
  };

  const navigatorGeneralStep: WorkspaceSpotlightTourStep = {
    id: 'navigator-general',
    phaseLabel: TOUR_PHASES.navigator,
    title: 'Use General for shared PCF mappings',
    focusLabel: 'General mapping scope',
    intro: 'General contains PCF attributes that apply across object types and require an explicit source.',
    details: [
      detail('How to use it', [
        'Choose General when the same mapping provides a shared PCF value across multiple object types.',
      ]),
      detail('Keep the scope precise', [
        'Use a specific object type when the source or PCF output applies only to that type.',
      ], 'caution'),
    ],
    nextAction: 'Open General only for shared PCF mappings. Otherwise, select the relevant object type.',
    targetRef: refs.generalNav,
    targetNode: { type: 'general' },
    fallbackRect: {
      top: 200,
      left: 28,
      width: 252,
      height: 52,
    },
  };

  const navigatorProcessStep: WorkspaceSpotlightTourStep = {
    id: 'navigator-process-conditions',
    phaseLabel: TOUR_PHASES.navigator,
    title: 'Open shared process metadata',
    focusLabel: 'Process conditions scope',
    intro: 'The Process conditions scope contains profile-level operating data used across the PCF output.',
    details: [
      detail('How to use it', [
        'Use this scope for pressure, temperature, tracing, or other operating values shared across the profile.',
      ]),
    ],
    nextAction: 'Open Process conditions when a profile-level source provides process metadata used across the PCF output.',
    targetRef: refs.processConditionsNav,
    targetNode: { type: 'processConditions' },
    fallbackRect: {
      top: 268,
      left: 28,
      width: 252,
      height: 52,
    },
  };

  const navigatorProjectStep: WorkspaceSpotlightTourStep = {
    id: 'navigator-project-parameters',
    phaseLabel: TOUR_PHASES.navigator,
    title: 'Open project export metadata',
    focusLabel: 'Project properties scope',
    intro: 'The Project properties scope contains document, client, and project-level values included in the PCF output.',
    details: [
      detail('How to use it', [
        'Use this scope for project identifiers, titles, area data, and other document-level values.',
      ]),
    ],
    nextAction: 'Open Project properties when the source is project-level information that should be included in the PCF output.',
    targetRef: refs.projectParametersNav,
    targetNode: { type: 'projectProperties' },
    fallbackRect: {
      top: 324,
      left: 28,
      width: 252,
      height: 52,
    },
  };

  const navigatorObjectTypesStep: WorkspaceSpotlightTourStep = {
    id: 'navigator-object-types',
    phaseLabel: TOUR_PHASES.navigator,
    title: 'Map PCF attributes by object type',
    focusLabel: 'Object type mapping scope',
    intro: 'Each object type contains the PCF mappings whose sources and output apply only to that type.',
    details: [
      detail('How to use it', [
        'Select an object type before changing its PCF targets or value sources.',
        'Add an object type only when it requires mappings that differ from the default scope.',
      ]),
      detail('Keep the profile consistent', [
        'Delete an object type only when its object-specific mappings are no longer required.',
      ], 'caution'),
    ],
    nextAction: 'Select the relevant object type, then add or remove object-specific scopes only when the profile configuration needs to change.',
    targetRef: refs.selectedObjectTypeNav,
    targetNode: objectTypeNode,
    fallbackRect: {
      top: 230,
      left: 28,
      width: 252,
      height: 48,
    },
  };

  const mappingCanvasStep: WorkspaceSpotlightTourStep = {
    id: 'mapping-canvas',
    phaseLabel: TOUR_PHASES.canvas,
    title: 'Define each source-to-PCF mapping',
    focusLabel: 'Mapping rows for the selected scope',
    intro: 'The mapping table defines the source, PCF target, and output behavior for each attribute.',
    details: [
      detail('Review the full mapping', [
        'Confirm the PCF target, required status, source type, and current value together.',
        'Treat values provided by the profile as the default mappings unless a different configuration is required.',
        'Define a valid source for every required attribute in the selected scope.',
      ]),
      detail('Export effect', [
        'Clear mappings allow Quick Fill and Validation to use the same source-to-PCF configuration consistently.',
      ], 'accent'),
    ],
    nextAction: 'Define the required mappings here, then use Quick Fill for repeated Revit data corrections and Validation to confirm export readiness.',
    targetRef: refs.mappingCanvas,
    relatedTargets: [selectedScopeRelatedTarget],
    relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
    targetNode: objectTypeNode,
    fallbackRect: {
      top: 728,
      left: 6,
      width: 279,
      height: 26,
    },
  };

  const typeColumnStep: WorkspaceSpotlightTourStep = {
    id: 'mapping-type-column',
    phaseLabel: TOUR_PHASES.canvas,
    title: 'Choose the value source type',
    focusLabel: 'Mapping source type column',
    intro: 'Type determines whether the PCF attribute gets its value from Revit, BOM data, a direct value, or a formula.',
    details: [
      detail('Match the source to the value', [
        'Choose the source type that provides the value written to the PCF attribute.',
      ]),
      detail('Keep scope and source type separate', [
        'The navigator selects the mapping scope, while Type determines how a row in that scope obtains its value.',
      ], 'caution'),
      detail('Review the row context', [
        'The selected scope identifies the object type, section, or status category where the mapping applies.',
        'Descriptions explain the purpose of the mapping but do not change its source or PCF target.',
      ], 'caution'),
    ],
    nextAction: 'Confirm the mapping scope, then change Type only when the current value source is incorrect.',
    targetRef: refs.typeColumn,
    relatedTargets: [selectedScopeRelatedTarget],
    relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
    targetNode: objectTypeNode,
    fallbackRect: {
      top: 248,
      left: 300,
      width: 200,
      height: 200,
    },
  };

  const tableControlsStep: WorkspaceSpotlightTourStep = {
    id: 'mapping-table-controls',
    phaseLabel: TOUR_PHASES.canvas,
    title: 'Control the visible mapping rows',
    focusLabel: 'Mapping row controls',
    intro: 'Use selection, row order, filters, and sorting to focus on the mappings you want to review or change.',
    details: [
      detail('How to use it', [
        'Filter by PCF, Revit, Type, or Fx to show the relevant source-to-PCF mappings.',
        'Sort or reorder rows without changing how the mappings are defined.',
      ]),
      detail('Select the intended rows', [
        'Select rows only after filtering the table to the mappings you want to work with.',
      ], 'caution'),
    ],
    nextAction: 'Narrow the table first, then edit only the source-to-PCF mappings that remain visible.',
    targetRef: refs.tableControls,
    targetNode: objectTypeNode,
    fallbackRect: {
      top: 228,
      left: 300,
      width: Math.max(window.innerWidth - 408, 360),
      height: 72,
    },
  };

  const pcfColumnStep: WorkspaceSpotlightTourStep = {
    id: 'mapping-pcf-column',
    phaseLabel: TOUR_PHASES.canvas,
    title: 'Confirm the PCF target attribute',
    focusLabel: 'PCF target attribute column',
    intro: 'The PCF column identifies the attribute written to the PCF output and indicates which attributes are required.',
    details: [
      detail('Review the target', [
        'Treat the PCF name as the exact attribute used in the exported file, not as a descriptive label.',
        'Define all required target attributes before continuing to Validation or ISO export.',
      ]),
    ],
    nextAction: 'Confirm the PCF target, then choose the source that will provide its value.',
    targetRef: refs.pcfColumn,
    relatedTargets: [selectedScopeRelatedTarget],
    relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
    targetNode: objectTypeNode,
    fallbackRect: {
      top: 248,
      left: 360,
      width: 320,
      height: 200,
    },
  };

  const revitColumnStep: WorkspaceSpotlightTourStep = {
    id: 'mapping-revit-column',
    phaseLabel: TOUR_PHASES.canvas,
    title: 'Bind a Revit parameter source',
    focusLabel: 'Revit source parameter column',
    intro: 'The Revit column identifies the model parameter that provides the value for the selected PCF attribute.',
    details: [
      detail('How to use it', [
        'Select the Revit parameter whose value should be written to the PCF attribute.',
        'Set Type to the appropriate Revit source type so PipeEX reads the value correctly.',
      ]),
    ],
    nextAction: 'After confirming the mapping scope and PCF target, select the Revit parameter that provides the value.',
    targetRef: refs.revitColumn,
    targetNode: objectTypeNode,
    fallbackRect: {
      top: 248,
      left: 728,
      width: 420,
      height: 200,
    },
  };

  const formulaColumnStep: WorkspaceSpotlightTourStep = {
    id: 'mapping-formula-column',
    phaseLabel: TOUR_PHASES.canvas,
    title: 'Set a direct value or formula source',
    focusLabel: 'Direct value and formula column',
    intro: 'Fx provides either a fixed value or a calculated result when Revit or BOM data is not the appropriate source.',
    details: [
      detail('How to use it', [
        'Enter a direct value when the same fixed value should be written for this mapping.',
        'Use the formula editor when the value must be calculated, and set Type to Formula.',
      ]),
      detail('Keep model cleanup separate', [
        'Do not use a direct value or formula to replace repeated Revit data corrections.',
      ], 'caution'),
    ],
    nextAction: 'Use Fx only when the mapping requires a fixed or calculated value. Use Quick Fill for repeated Revit data corrections.',
    targetRef: refs.formulaColumn,
    targetNode: objectTypeNode,
    fallbackRect: {
      top: 248,
      left: Math.max(window.innerWidth - 240, 980),
      width: 160,
      height: 200,
    },
  };

  const rowActionsStep: WorkspaceSpotlightTourStep = {
    id: 'mapping-row-actions',
    phaseLabel: TOUR_PHASES.canvas,
    title: 'Add or remove mapping rows',
    focusLabel: 'Mapping row action controls',
    intro: 'Row actions change which PCF attributes are mapped in the selected scope. They do not modify the source data.',
    details: [
      detail('How to use it', [
        'Add attribute creates a new PCF mapping in the selected scope.',
        'Delete removes a mapping that is no longer required in the active profile.',
      ]),
      detail('Change mappings carefully', [
        'Add a row only for a valid PCF target, and delete one only after confirming that the mapping is no longer required.',
      ], 'caution'),
    ],
    nextAction: 'Add or remove mapping rows only after confirming the mapping scope, PCF target, and expected output.',
    targetRef: refs.rowActions,
    targetNode: objectTypeNode,
    fallbackRect: {
      top: 430,
      left: 300,
      width: 180,
      height: 52,
    },
  };

  const generalSectionStep: WorkspaceSpotlightTourStep = {
    id: 'general-mapping-section',
    phaseLabel: TOUR_PHASES.canvas,
    title: 'Define shared mapping rows',
    focusLabel: 'General PCF mapping editor',
    intro: 'The General scope contains shared PCF mappings that apply across multiple object types in the active profile.',
    details: [
      detail('How to use it', [
        'Define each shared PCF target and choose its source: Revit, BOM, a direct value, or a formula.',
      ]),
    ],
    nextAction: 'Define the shared PCF mappings here, then use Quick Fill for repeated Revit data corrections.',
    targetRef: refs.generalSection,
    relatedTargets: [generalRelatedTarget],
    relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
    targetNode: { type: 'general' },
    fallbackRect: {
      top: 268,
      left: 324,
      width: Math.max(window.innerWidth - 408, 360),
      height: Math.max(window.innerHeight - 336, 240),
    },
  };

  const advancedSectionStep: WorkspaceSpotlightTourStep = {
    id: 'advanced-mapping-section',
    phaseLabel: TOUR_PHASES.sections,
    title: 'Keep rule-based output in Advanced settings',
    focusLabel: 'Advanced settings rule editor',
    intro: 'The Advanced settings scope contains rule-based output, including status logic, that does not belong in a standard object mapping.',
    details: [
      detail('How to use it', [
        'Use Advanced settings when a rule combines multiple inputs or generates status metadata for PCF output.',
      ]),
      detail('Keep simple mappings simple', [
        'Use the relevant object type when a single source provides a PCF target directly.',
      ], 'caution'),
    ],
    nextAction: 'Open Advanced settings only when the PCF output depends on rule-based or status logic.',
    targetRef: refs.advancedSection,
    relatedTargets: [advancedRelatedTarget],
    relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
    targetNode: { type: 'advanced' },
    fallbackRect: {
      top: 268,
      left: 324,
      width: Math.max(window.innerWidth - 408, 360),
      height: Math.max(window.innerHeight - 336, 240),
    },
  };

  const navigatorStatusBuilderStep: WorkspaceSpotlightTourStep = {
    id: 'navigator-status-builder',
    phaseLabel: TOUR_PHASES.sections,
    title: 'Open Project phase statuses',
    focusLabel: isMobile ? 'Project phase statuses with mobile scope context' : 'Project phase statuses in the navigator',
    intro: 'The Project phase statuses section contains the rules that generate status metadata for the PCF output.',
    details: [
      detail('How to use it', [
        'Expand Advanced settings to open Project phase statuses and its status categories.',
        'Use Project phase statuses when multiple inputs determine the status metadata written to the PCF output.',
      ]),
    ],
    nextAction: 'Open Project phase statuses, then select the relevant status category and review the rules that generate its metadata.',
    targetRef: refs.statusBuilderNav,
    targetNode: { type: 'statusBuilder' },
    fallbackRect: {
      top: 360,
      left: 28,
      width: 252,
      height: 52,
    },
  };

  const statusBuilderSectionStep: WorkspaceSpotlightTourStep = {
    id: 'status-builder-section',
    phaseLabel: TOUR_PHASES.sections,
    title: 'Define how status metadata is generated',
    focusLabel: 'Status output rule builder',
    intro: 'This editor defines the input conditions and rules used to generate profile-level status metadata.',
    details: [
      detail('Configure the status rules', [
        'Define the inputs and rules that generate the required status value in the PCF output.',
      ]),
      detail('Keep object mappings separate', [
        'For missing OBJECT-TYPE or SKEY mappings, return to the relevant object mapping. For repeated Revit data corrections, use Quick Fill.',
      ], 'caution'),
    ],
    nextAction: 'Complete the status rule here, then return to the relevant object mapping for direct PCF attribute mappings.',
    targetRef: refs.statusBuilderSection,
    relatedTargets: [statusBuilderRelatedTarget],
    relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
    targetNode: { type: 'statusBuilder' },
    fallbackRect: {
      top: 268,
      left: 324,
      width: Math.max(window.innerWidth - 408, 360),
      height: Math.max(window.innerHeight - 336, 240),
    },
  };

  const navigatorStatusCategoryStep: WorkspaceSpotlightTourStep | null = firstStatusCategoryId
    ? {
        id: 'navigator-status-category',
        phaseLabel: TOUR_PHASES.sections,
        title: 'Choose the relevant status category',
        focusLabel: isMobile ? 'Status category with mobile scope context' : 'Status category in the navigator',
        intro: 'Each status category contains the inputs and rules used to generate a specific type of status metadata.',
        details: [
          detail('How to use it', [
            'Select the category whose source conditions and output match the required status metadata.',
          ]),
        ],
        nextAction: 'Open the relevant category before changing its status rules or descriptions.',
        targetRef: refs.statusCategoryNav,
        targetNode: { type: 'statusCategory', name: firstStatusCategoryId },
        fallbackRect: {
          top: 420,
          left: 28,
          width: 252,
          height: 48,
        },
      }
    : null;

  const statusCategorySectionStep: WorkspaceSpotlightTourStep | null = firstStatusCategoryId
    ? {
        id: 'status-category-section',
        phaseLabel: TOUR_PHASES.sections,
        title: 'Edit one status category',
        focusLabel: 'Status category editor',
        intro: 'This editor keeps the source conditions, descriptions, and output rules for one status category in one place.',
        details: [
          detail('Keep the category consistent', [
            "Edit fields and rules only when they apply to the selected category's input conditions and status output.",
          ], 'caution'),
        ],
        nextAction: 'Complete the current category, then select another status category before changing a different status output.',
        targetRef: refs.statusCategorySection,
        relatedTargets: [statusCategoryRelatedTarget],
        relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
        targetNode: { type: 'statusCategory', name: firstStatusCategoryId },
        fallbackRect: {
          top: 268,
          left: 324,
          width: Math.max(window.innerWidth - 408, 360),
          height: Math.max(window.innerHeight - 336, 240),
        },
      }
    : null;

  const processConditionsBodyStep: WorkspaceSpotlightTourStep = {
    id: 'process-conditions-section',
    phaseLabel: TOUR_PHASES.sections,
    title: 'Set profile-level process metadata',
    focusLabel: 'Process metadata source editor',
    intro: 'This section defines shared process and operating metadata for the active profile without repeating it across object-specific mappings.',
    details: [
      detail('How to use it', [
        'Enter pressure, temperature, tracing, and related process values from the appropriate source.',
        'Use a single profile-level value when the same metadata applies across the PCF output.',
      ]),
      detail('Keep object mappings separate', [
        'Return to the relevant object mapping when the source or PCF output applies only to a specific object type or attribute.',
      ], 'caution'),
    ],
    nextAction: 'Define the shared process metadata here, then confirm the resulting PCF output.',
    targetRef: refs.processConditions,
    relatedTargets: [processConditionsRelatedTarget],
    relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
    targetNode: {
      type: 'processConditions',
    },
    fallbackRect: {
      top: 268,
      left: 324,
      width: Math.max(window.innerWidth - 408, 360),
      height: Math.max(window.innerHeight - 336, 240),
    },
  };

  const projectParametersBodyStep: WorkspaceSpotlightTourStep = {
    id: 'project-parameters-section',
    phaseLabel: TOUR_PHASES.sections,
    title: 'Set project and document metadata',
    focusLabel: 'Project export metadata editor',
    intro: 'This section defines project, document, and client metadata included in the PCF output separately from object-specific mappings.',
    details: [
      detail('How to use it', [
        'Enter project identifiers, names, area data, and other document-level values from the appropriate source.',
        'Keep this metadata centralized so object-specific mappings remain focused on object attributes.',
      ]),
      detail('Keep mapping issues separate', [
        'Do not use project metadata to compensate for unresolved Revit, BOM, or formula mappings.',
      ], 'caution'),
    ],
    nextAction: 'Complete the project metadata here, then return to Mapping for object-specific source mappings.',
    targetRef: refs.projectParameters,
    relatedTargets: [projectPropertiesRelatedTarget],
    relatedTargetsLabel: TOUR_RELATED_LABELS.scope,
    targetNode: {
      type: 'projectProperties',
    },
    fallbackRect: {
      top: 268,
      left: 324,
      width: Math.max(window.innerWidth - 408, 360),
      height: Math.max(window.innerHeight - 336, 240),
    },
  };

  const actionsStep: WorkspaceSpotlightTourStep = {
    id: 'actions',
    phaseLabel: TOUR_PHASES.handoff,
    title: 'Apply the mapping and continue',
    focusLabel: 'Apply mapping and continue actions',
    intro: 'Use the footer actions to apply the current source-to-PCF mappings and either continue reviewing or move to the next workflow.',
    details: [
      detail('How to use it', [
        'Use Apply mapping and stay to apply the latest mappings and continue reviewing other scopes.',
        'Use Save mapping and close when the mapping is complete and the remaining work belongs in Quick Fill or Validation.',
      ]),
      detail('Before you continue', [
        'Make sure every required PCF target has a clearly defined source, and each remaining task has a clear next workflow.',
      ], 'accent'),
    ],
    nextAction: 'Use Apply mapping and stay to continue reviewing, or Save mapping and close to proceed to Quick Fill or Validation.',
    targetRef: refs.footerActions,
    fallbackRect: {
      top: Math.max(window.innerHeight - 138, 24),
      left: 24,
      width: Math.max(window.innerWidth - 48, 360),
      height: 96,
    },
  };

  const statusCategorySteps: WorkspaceSpotlightTourStep[] = [
    ...(navigatorStatusCategoryStep && statusCategorySectionStep
      ? [navigatorStatusCategoryStep, statusCategorySectionStep]
      : []),
  ];

  const coreSteps: WorkspaceSpotlightTourStep[] = [
    ...(isMobile ? [mobileMenuStep] : []),
    navigationStep,
    navigatorGeneralStep,
    navigatorProcessStep,
    navigatorProjectStep,
    navigatorObjectTypesStep,
    mappingCanvasStep,
    tableControlsStep,
    pcfColumnStep,
    revitColumnStep,
    typeColumnStep,
    formulaColumnStep,
    rowActionsStep,
    generalSectionStep,
    advancedSectionStep,
    navigatorStatusBuilderStep,
    statusBuilderSectionStep,
    ...statusCategorySteps,
    processConditionsBodyStep,
    projectParametersBodyStep,
    actionsStep,
  ];

  return coreSteps;
};
